import { getInteractionModality } from "@ui/primitives/shared";
import { useEffect, useRef } from "react";

/**
 * Auto-focuses the follow-up input when it becomes visible — but only on pointer
 * interactions (mouse / touch). Keyboard and screen reader users tab to the input
 * naturally; force-stealing focus from them would hijack their flow.
 *
 * The returned ref can point either to the input/textarea itself, or to a
 * wrapper that contains exactly one input/textarea descendant.
 */
export function useFollowUpAutoFocus({ isVisible }: { isVisible: boolean }) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isVisible) return;
    if (getInteractionModality() !== "pointer") return;

    const timeoutId = setTimeout(() => {
      const el = ref.current;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        el.focus();
      } else {
        el?.querySelector<HTMLElement>("textarea, input")?.focus();
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [isVisible]);

  return ref;
}
